import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { MailchimpIntegration } from 'api/clients/ExternalIntegrationsClient'
import { SendToInput } from 'components/EmailBlock/inputs/SendToInput'
import { useUpdateExternalIntegration } from 'hooks/queries/externalIntegrations/useUpdateExternalIntegration'
import { useMailchimpFetchAudienceLists } from 'hooks/queries/externalIntegrations/useMailchimpFetchAuidence'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useState, useEffect } from 'react'
import { AudienceList } from 'hooks/queries/externalIntegrations/useMailchimpFetchAuidence'
import { MailchimpUser } from 'components/EmailBlock/types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from '../IntegrationSuccessModal/IntegrationSuccessModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AutopilotInfo } from 'components/IntegrationSuccessModal/AutopilotInfo'
import { RoundedPlatformIcon } from 'components/RoundedPlatformIcon/RoundedPlatformIcon'

interface Props {
  integration: MailchimpIntegration
  onClose: () => void
  onPressOutside?: () => void
}

export const MailchimpConnectedSuccessModal = ({ integration, onClose }: Props) => {
  const { closeModal } = useModals()
  const workspace = useCurrentWorkspace()
  const workspaceId = useCurrentWorkspaceId()
  const updateExternalIntegration = useUpdateExternalIntegration()

  const [selectedContacts, setSelectedContacts] = useState<AudienceList | null>(null)

  const {
    data: audienceLists,
    isLoading: audienceLoading,
    error: audienceError,
  } = useMailchimpFetchAudienceLists(workspaceId, true)

  const mailchimpProfile = integration.additional_data as MailchimpUser

  useEffect(() => {
    if (audienceLists && audienceLists.length > 0 && !selectedContacts) {
      setSelectedContacts(audienceLists[0])
    }
  }, [audienceLists, selectedContacts])

  const handleConfirm = async () => {
    if (!selectedContacts) {
      showToastMessage('Please select an audience list', 'error')
      return
    }

    try {
      await updateExternalIntegration.mutateAsync({
        id: integration.id,
        additional_data: {
          default_list_id: selectedContacts.id,
          default_from_name: mailchimpProfile.account_name,
        } as Partial<MailchimpUser>,
      })

      showToastMessage('Default audience list saved successfully!', 'success')

      closeModal()
      onClose()
    } catch (error) {
      showToastMessage('Failed to save default audience list. Please try again.', 'error')
    }
  }

  const handleContactsChange = (contacts: AudienceList) => {
    setSelectedContacts(contacts)
  }

  const isAutopilot = workspace?.is_virtual_marketer

  return (
    <Modal.Root
      size="sm"
      blaze
      onPressOutside={() => {}}
      onClose={() => {}}
      aria-label="Configure Mailchimp Integration"
    >
      <Modal.Header title="Mailchimp account connected!" />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.titleSection}>
            <div className={Styles.iconContainer}>
              <RoundedPlatformIcon platform={integration.provider} size={24} />
            </div>
            <Heading level={2}>Your Mailchimp content will start autoposting</Heading>
          </div>
          {isAutopilot && (
            <AutopilotInfo integration={integration} onClose={onClose} requiredFieldName="audience list" />
          )}
          <div className={Styles.fieldsContainer}>
            <div className={Styles.fieldBox}>
              <Heading level={4}>Choose your default recipients for all emails</Heading>
              <div className={Styles.inputWrapper}>
                <SendToInput
                  audienceLoading={audienceLoading}
                  audienceError={audienceError}
                  audienceLists={audienceLists || []}
                  selectedContacts={
                    selectedContacts ||
                    (audienceLists && audienceLists.length > 0 ? audienceLists[0] : null) ||
                    (audienceLists?.[0] as AudienceList)
                  }
                  onContactsChange={handleContactsChange}
                  data-testid="send-to-input"
                />
              </div>
            </div>
            <Text variant="secondary" color="var(--dark-60)">
              You can change these settings anytime from the Integrations page or when editing a post.
            </Text>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleConfirm}
            isDisabled={!selectedContacts || updateExternalIntegration.isLoading}
          >
            {updateExternalIntegration.isLoading ? 'Saving...' : 'Confirm'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
